import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import type { Vehicle } from "@/domain/models/vehicle";
import { formatMiles, formatPrice } from "@/domain/models/vehicle";
import { siteLocation } from "@/data/location";
import { useI18n } from "@/i18n";
import { Phone, Mail, MessageCircle, Download, ChevronLeft, ChevronRight, Check, X } from "lucide-react";

type Props = {
  vehicle: Vehicle | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function VehicleModal({ vehicle, open, onOpenChange }: Props) {
  const { t } = useI18n();
  const [imgIdx, setImgIdx] = useState(0);

  if (!vehicle) return null;
  const gallery = vehicle.gallery.length ? vehicle.gallery : [vehicle.image];
  const next = () => setImgIdx((i) => (i + 1) % gallery.length);
  const prev = () => setImgIdx((i) => (i - 1 + gallery.length) % gallery.length);

  const specs: [string, string][] = [
    [t.vehicle.year, String(vehicle.year)],
    [t.vehicle.body, vehicle.body],
    [t.vehicle.condition, vehicle.condition],
    [t.vehicle.mileage, formatMiles(vehicle.mileage)],
    [t.vehicle.power, `${vehicle.power} hp`],
    [t.vehicle.acceleration, `${vehicle.acceleration}s`],
    [t.vehicle.topSpeed, `${vehicle.topSpeed} km/h`],
    [t.vehicle.drivetrain, vehicle.drivetrain],
    [t.vehicle.transmission, vehicle.transmission],
    [t.vehicle.fuel, vehicle.fuel],
    [t.vehicle.exterior, vehicle.color],
    [t.vehicle.availability, vehicle.available ? t.vehicle.inStock : t.vehicle.reserved],
  ];

  const monthly = Math.round((vehicle.price * 0.012) / 1) * 1;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setImgIdx(0);
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-h-[92vh] w-[96vw] max-w-6xl overflow-y-auto border-border bg-card p-0 [&>button]:hidden">
        <DialogTitle className="sr-only">{`${vehicle.year} ${vehicle.brand} ${vehicle.model}`}</DialogTitle>
        <DialogDescription className="sr-only">{vehicle.description}</DialogDescription>

        <button
          onClick={() => onOpenChange(false)}
          aria-label={t.vehicle.close}
          className="absolute right-4 top-4 z-20 grid h-10 w-10 place-items-center rounded-full bg-background/80 text-foreground backdrop-blur transition hover:bg-background"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="grid gap-0 lg:grid-cols-[1.35fr_1fr]">
          <div className="relative bg-black">
            <div className="relative aspect-[4/3] w-full overflow-hidden lg:aspect-auto lg:h-full lg:min-h-[560px]">
              <img
                key={gallery[imgIdx]}
                src={gallery[imgIdx]}
                alt={`${vehicle.model} ${imgIdx + 1}`}
                className="h-full w-full animate-fade-in object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/60 to-transparent" />

              {gallery.length > 1 && (
                <>
                  <button
                    onClick={prev}
                    aria-label={t.vehicle.prevImage}
                    className="absolute left-4 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-background/80 text-foreground backdrop-blur transition hover:bg-background"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={next}
                    aria-label={t.vehicle.nextImage}
                    className="absolute right-4 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-background/80 text-foreground backdrop-blur transition hover:bg-background"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}

              {vehicle.tag && (
                <span className="absolute left-4 top-4 rounded-full bg-primary/95 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-primary-foreground">
                  {vehicle.tag}
                </span>
              )}
            </div>

            {gallery.length > 1 && (
              <div className="flex gap-2 border-t border-border/40 bg-background/50 p-3">
                {gallery.map((g, i) => (
                  <button
                    key={g + i}
                    onClick={() => setImgIdx(i)}
                    className={`relative h-14 w-20 shrink-0 overflow-hidden rounded-md border transition ${
                      i === imgIdx
                        ? "border-primary ring-2 ring-primary/40"
                        : "border-border opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={g} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col p-6 sm:p-8">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                {vehicle.brand} · {vehicle.condition}
              </div>
              <h2 className="mt-1 font-display text-2xl font-bold sm:text-3xl">
                {vehicle.year} {vehicle.model}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{vehicle.description}</p>
            </div>

            <div className="mt-6 flex items-end justify-between rounded-xl border border-border bg-surface p-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {t.vehicle.price}
                </div>
                <div className="font-display text-3xl font-bold text-primary">
                  {formatPrice(vehicle.price)}
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {t.vehicle.fromMo}
                </div>
                <div className="font-display text-lg font-semibold">{formatPrice(monthly)}</div>
              </div>
            </div>

            <div className="mt-6">
              <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {t.vehicle.specifications}
              </h3>
              <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {specs.map(([k, v]) => (
                  <div
                    key={k}
                    className="flex items-baseline justify-between border-b border-border/60 py-1.5"
                  >
                    <dt className="text-muted-foreground">{k}</dt>
                    <dd className="font-medium">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-6">
              <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {t.vehicle.features}
              </h3>
              <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                {vehicle.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-2">
              <a
                href={`https://wa.me/${siteLocation.whatsapp}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-border bg-surface text-xs font-semibold transition hover:border-primary hover:text-primary"
              >
                <MessageCircle className="h-4 w-4" /> {t.vehicle.whatsapp}
              </a>
              <a
                href={`tel:${siteLocation.phoneTel}`}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-border bg-surface text-xs font-semibold transition hover:border-primary hover:text-primary"
              >
                <Phone className="h-4 w-4" /> {t.vehicle.call}
              </a>
              <a
                href={`mailto:${siteLocation.email}?subject=Inquiry: ${vehicle.year} ${vehicle.model}`}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-border bg-surface text-xs font-semibold transition hover:border-primary hover:text-primary"
              >
                <Mail className="h-4 w-4" /> {t.vehicle.email}
              </a>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <button className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-primary text-sm font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition hover:-translate-y-0.5">
                {t.vehicle.reserveNow}
              </button>
              <button className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-border bg-transparent text-sm font-semibold transition hover:bg-surface">
                <Download className="h-4 w-4" /> {t.vehicle.brochure}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
